import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";

import PageHeader from "components/UniversalComponents/PageHeader";
import { MainBody, OverlayLoader, Ptag } from "components/core";
import { ClaimsMisHeader, SearchBarWrapper } from "../../Claims/styles";
import http from "services/httpService";
import FilterBar from "pages/Claims/Mis/FilterBar";
import ClaimsTable from "pages/Claims/components/ClaimsTable";
import SearchBar from "pages/Claims/Mis/SearchBar";
import BaseBreadcrumbs from "components/core/BaseBreadcrumbs";
import ClaimMisDownload from "./ClaimMisDownload";
import { ClaimSearchFilterWrapper } from "./styles";
import { useLocation, useNavigate } from "react-router";

function ClaimsEmployeeMis() {
  const [loading, setLoading] = useState(true);
  const [searchStr, setSearchStr] = useState("");
  const [headers, setHeaders] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filterValue, setFilterValue] = useState([]);
  const [filterArray, setFilterArray] = useState(new Set());
  const [paginationData, setPaginationData] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const didMountRef = useRef(false);
  const timerRef = useRef(null);
  const defaultOffset = 20;
  const params = new URLSearchParams(location.search);

  const fetchData = async () => {
    setContentLoading(true);

    const pageStarting = params.get("start") || 1;
    const pageEnd = params.get("end") || defaultOffset;
    const payload = {
      token: Number(pageStarting),
      offset: Number(pageEnd),
    };

    if (searchStr) {
      payload.search_query_string = searchStr;
    }

    const selectedFilters = Array.from(filterArray || []);

    if (selectedFilters.length > 0) {
      payload.filter_query_string = JSON.stringify(selectedFilters);
    }

    try {
      const { data } = await http.get("business/get_mis_claims/", {
        params: payload,
      });

      setHeaders(data?.headers || []);
      setTableData(data?.claim_mis || []);
      setPaginationData(data);

      if (selectedFilters.length === 0 && !searchStr) {
        statusArray(data?.claim_mis || []);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
      setContentLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [location.search]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const currentSearch = location.search.startsWith("?")
      ? location.search.slice(1)
      : location.search;

    const nextParams = new URLSearchParams(location.search);
    nextParams.set("start", 1);
    nextParams.set("end", defaultOffset);
    const nextSearch = nextParams.toString();

    timerRef.current = setTimeout(() => {
      if (currentSearch === nextSearch) {
        fetchData();
        return;
      }

      navigate({
        pathname: location.pathname,
        search: nextSearch,
      });
    }, 600);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [searchStr, filterArray, location.pathname, navigate]);

  const statusArray = (value) => {
    const statusReducer = (acc, val) => {
      if (!acc.includes(val.claim_status)) {
        acc.push(val.claim_status);
      }
      return acc;
    };

    const accurateValue = value.reduce(statusReducer, []).sort();
    setFilterValue(accurateValue);
    return accurateValue;
  };

  const searchHandler = (e) => {
    const value = e.target.value;
    setSearchStr(value);
  };

  const nextPageHandler = () => {
    const nextParams = new URLSearchParams(location.search);

    if (paginationData?.token) {
      nextParams.set("start", paginationData.token);
      nextParams.set("end", defaultOffset);
      navigate({
        pathname: location.pathname,
        search: nextParams.toString(),
      });
    }
  };

  const prevPageHandler = () => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("start", Number(nextParams.get("start")) - 1);
    nextParams.set("end", defaultOffset);
    navigate({
      pathname: location.pathname,
      search: nextParams.toString(),
    });
  };

  const pageNumberHandler = () => {
    let startingPage = 1;
    let endPage = paginationData?.total_records || 0;

    if (params.get("start")) {
      startingPage = params.get("start");
    }

    return [startingPage, endPage];
  };

  const isValidPrevPageNumber = () => {
    const pageStartingNum = params.get("start") || 1;
    return Number(pageStartingNum) > 1;
  };

  const isValidNextPageNumber = () => {
    return Boolean(paginationData?.token);
  };

  const shouldPaginate = Number(paginationData?.total_records || 0) > defaultOffset;

  const crumbs = useMemo(() => {
    return [
      {
        name: "Claims",
        path: "/v1/claims",
      },
      { 
        name: "Claim MIS",
        active: true
        // path: "/v1/claims/mis"
      },
    ];
  }, []);

  if (loading) {
    return <OverlayLoader />;
  }

  return (
    <>
      <BaseBreadcrumbs breadcrumbs={crumbs} />
      <SearchBarWrapper>
        <ClaimSearchFilterWrapper>
          <SearchBar value={searchStr} onChange={searchHandler} />
          <FilterBar filters={filterValue} onChange={setFilterArray} />
        </ClaimSearchFilterWrapper>
        <ClaimMisDownload />
      </SearchBarWrapper>

      <>
        {/* <ClaimsMisHeader>
          <PageHeader header="Claims MIS" prev_page />
        </ClaimsMisHeader> */}

        <Box>
          <ClaimsTable headers={headers} rows={tableData} />
          {shouldPaginate && <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "18px",
            }}
          >
            <Ptag
              fontWeigh="500"
              fontFamily="Montserrat"
              color="rgba(0, 0, 0, 1)"
              fontSize="14px"
              lineHeight="20px"
            >
              {"Page"}{" "}
              {`${pageNumberHandler()[0]} of ${Math.ceil(
                Number(pageNumberHandler()[1]) / defaultOffset
              )}`}
            </Ptag>
            <Box sx={{ display: "flex", gap: "12px" }}>
              <button
                style={{
                  opacity: contentLoading ? 0.4 : isValidPrevPageNumber() ? 1 : 0.4,
                }}
                disabled={!isValidPrevPageNumber() || contentLoading}
                className="pagination-button-css pagination-prev-button-css"
                onClick={prevPageHandler}
              >
                Previous
              </button>
              <button
                style={{
                  opacity: contentLoading ? 0.4 : isValidNextPageNumber() ? 1 : 0.4,
                }}
                disabled={!isValidNextPageNumber() || contentLoading}
                className="pagination-button-css pagination-next-button-css"
                onClick={nextPageHandler}
              >
                Next
              </button>
            </Box>
          </Box>}
        </Box>
      </>
    </>
  );
}

export default ClaimsEmployeeMis;
